import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Pagination from "../../components/admin/Pagination";
import TableScroll from "../../components/TableScroll";
import { fetchOrders } from "../../services/orderApi";
import "../../styles/admin-common.css";

const PAGE_SIZE = 10;
const STATUS_OPTIONS = [
  "", "PENDING", "ASSIGNED", "BUYER_ON_THE_WAY", "INSPECTING",
  "AWAITING_CUSTOMER_APPROVAL", "READY_TO_PAY", "NEGOTIATING", "ONGOING",
  "COMPLETED", "CANCELLED",
];

const formatDate = (d) => (d ? new Date(d).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "-");

const Orders = () => {
  const [searchParams] = useSearchParams();
  const filterUserId = searchParams.get("userId") || "";
  const filterBuyerId = searchParams.get("buyerId") || "";
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setLoading(true);
    setError("");
    fetchOrders({
      page: currentPage,
      size: PAGE_SIZE,
      status: statusFilter || undefined,
      search: debouncedSearch || undefined,
      userId: filterUserId || undefined,
      buyerId: filterBuyerId || undefined,
    })
      .then((data) => {
        setOrders(data.content || []);
        setTotalPages(data.pagination?.totalPages || 1);
      })
      .catch((err) => {
        setError(err.message);
        setOrders([]);
      })
      .finally(() => setLoading(false));
  }, [currentPage, statusFilter, debouncedSearch, filterUserId, filterBuyerId]);

  const filterLabel = filterUserId
    ? `Customer #${filterUserId}`
    : filterBuyerId
      ? `Vendor #${filterBuyerId}`
      : null;

  return (
    <div className="admin-page">
      <h2>Orders</h2>
      <p className="subtitle">
        Search by order #, tracking, device, customer, or vendor. Assign and manage from order details.
      </p>
      {filterLabel && (
        <p className="warning-msg">
          Filtered by {filterLabel}.{" "}
          <button type="button" className="admin-btn link" onClick={() => navigate("/orders")}>
            Clear filter
          </button>
        </p>
      )}

      <div className="admin-filters">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
          placeholder="Search orders..."
        />
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(0); }}>
          <option value="">All statuses</option>
          {STATUS_OPTIONS.filter(Boolean).map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
          ))}
        </select>
      </div>

      {error && <p className="error-msg">{error}</p>}
      {loading ? (
        <div className="admin-loading">Loading orders</div>
      ) : (
        <>
          <TableScroll>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Status</th>
                <th>Customer</th>
                <th>Device</th>
                <th>Quoted</th>
                <th>Final</th>
                <th>Pickup</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={8} className="empty-msg">No orders found</td></tr>
              )}
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>
                    <strong>{order.orderNumber}</strong>
                    {order.trackingId && <div style={{ fontSize: 12, color: "#64748b" }}>{order.trackingId}</div>}
                  </td>
                  <td>
                    <span className={`status-badge status-${order.status}`}>
                      {order.status?.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td>{order.orderUserResponse?.name || order.orderUserResponse?.email || "-"}</td>
                  <td>{order.brandName} {order.deviceModelName}</td>
                  <td>₹{order.quotedPrice ?? "-"}</td>
                  <td>₹{order.finalPrice ?? "-"}</td>
                  <td>{formatDate(order.pickupDate)}</td>
                  <td className="actions-cell">
                    <button type="button" className="admin-btn" onClick={() => navigate(`/order/${order.id}`)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableScroll>
          <Pagination page={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </>
      )}
    </div>
  );
};

export default Orders;
